import { CidadaoSelectFieldModel, EnderecoFieldGroupModel, SimNaoEnum, simNaoEnumToBoolean } from 'components/form'
import { EnderecoIndigenaFieldGroupModel } from 'components/form/field/enderecoindigena/model-enderecoIndigena'
import {
  AnimaisDomicilioEnum,
  AnimaisImovel,
  CidadaoTerritorioBasico,
  NacionalidadeEnum,
  RacaCorDbEnum,
  TipoImovelEnum,
} from 'graphql/types.generated'
import { isEqual } from 'lodash'
import { isEmpty, isUndefinedOrNull } from 'util/checks'

import {
  AnimaisImovelFormModel,
  CadastrarCidadaoFamiliaFormModel,
  CadastrarEditarFamiliaFormModel,
  CadastroImovelCacheState,
  CidadaoTerritorioFormModel,
  CidadaoTerritorioModel,
  FamiliaFormModel,
  tipoImoveisResidenciais,
  tipoImoveisResidenciaisExcetoDomicilio,
} from './model-cadastroImovel'

export const simNaoEnumToBooleanWrapper = (simNaoEnumValue: SimNaoEnum) => {
  return simNaoEnumToBoolean[simNaoEnumValue ?? SimNaoEnum.NAO]
}

export const booleanToSimNaoEnum = (bool: boolean | undefined | null): SimNaoEnum | undefined => {
  if (bool === true) return SimNaoEnum.SIM
  if (bool === false) return SimNaoEnum.NAO
  return undefined
}
export const buildAnimais = (animaisImovel: AnimaisImovel) => {
  const animais: AnimaisDomicilioEnum[] = []
  animaisImovel.possuiCachorro && animais.push(AnimaisDomicilioEnum.CACHORRO)
  animaisImovel.possuiGato && animais.push(AnimaisDomicilioEnum.GATO)
  animaisImovel.possuiPassaro && animais.push(AnimaisDomicilioEnum.PASSARO)
  animaisImovel.possuiMacaco && animais.push(AnimaisDomicilioEnum.MACACO)
  animaisImovel.possuiGalinha && animais.push(AnimaisDomicilioEnum.GALINHA)
  animaisImovel.possuiPorco && animais.push(AnimaisDomicilioEnum.PORCO)
  animaisImovel.possuiRepteis && animais.push(AnimaisDomicilioEnum.REPTEIS)
  animaisImovel.possuiOutros && animais.push(AnimaisDomicilioEnum.OUTRO)
  return animais
}

export const evaluatePossuiAnimal = (animaisNoDomicilio: AnimaisImovelFormModel, animal: AnimaisDomicilioEnum) =>
  animaisNoDomicilio?.animais?.indexOf(animal) !== -1

export const isTipoImovelDomicilio = (tipoImovelEnum: TipoImovelEnum) => tipoImovelEnum === TipoImovelEnum.DOMICILIO

export const isTipoImovelResidencialExcetoDomicilio = (tipoImovel: TipoImovelEnum) =>
  tipoImoveisResidenciaisExcetoDomicilio.includes(tipoImovel)

export const isTipoImovelResidencial = (tipoImovel: TipoImovelEnum) => tipoImoveisResidenciais.includes(tipoImovel)

export const possuiOutrosProfissionais = (possuiOutrosProfissionais: string) => {
  if (isUndefinedOrNull(possuiOutrosProfissionais)) return null
  else return possuiOutrosProfissionais === SimNaoEnum.SIM
}

export const possuiOutrosProfissionaisToSimNaoEnum = (possuiOutrosProfissionais: boolean) => {
  if (isUndefinedOrNull(possuiOutrosProfissionais)) return null
  return possuiOutrosProfissionais ? SimNaoEnum.SIM : SimNaoEnum.NAO
}

export const possuiCidadaoPresenteOutrasFamilias = (
  familias: Array<FamiliaFormModel>,
  cidadaoId: ID,
  familiaId: ID
): boolean => {
  const outrasFamilias = familias?.filter((familia) => familia._id !== familiaId)
  if (!outrasFamilias) return false

  return outrasFamilias.some(
    (familia) => familia.responsavelId === cidadaoId || isCidadaoPresenteFamilia(familia, cidadaoId)
  )
}

export const isCidadaoPresenteFamilia = (familia: FamiliaFormModel, cidadaoId: ID) =>
  familia.cidadaos.some((cidadao) => cidadao.id === cidadaoId)

export const cidadaoIdByCpfCns = (cpfCns: string, cidadaos: ReadonlyArray<CidadaoTerritorioBasico>) => {
  return cidadaos.find((cidadao) => cidadao.cpf === cpfCns || cidadao.cns === cpfCns)?.id
}

export const getResponsavelDependentesForm = (responsavelId: ID, cidadaos: Array<CidadaoTerritorioFormModel>) => {
  return cidadaos?.reduce(
    (partition, cidadao) => {
      if (cidadao.id === responsavelId) {
        partition.responsavelFormModel = cidadao
      } else {
        partition.dependentesFormModel.push(cidadao)
      }
      return partition
    },
    { responsavelFormModel: null, dependentesFormModel: [] }
  )
}

export const setCadastroCidadaoCacheState = (
  form: CadastrarCidadaoFamiliaFormModel | null,
  renderAfterRedirect: boolean,
  cache: CadastroImovelCacheState
): CadastroImovelCacheState => ({
  ...cache,
  familiaCidadaoModalState: {
    ...cache?.familiaCidadaoModalState,
    cadastroCidadaoModal: {
      form: form as CadastrarCidadaoFamiliaFormModel,
      modalOpen: renderAfterRedirect,
    },
  },
})

export const setCadastroFamiliaCacheState = (
  form: CadastrarEditarFamiliaFormModel | null,
  renderAfterRedirect: boolean,
  cache: CadastroImovelCacheState,
  editionId?: ID
) => ({
  ...cache,
  familiaCidadaoModalState: {
    ...cache?.familiaCidadaoModalState,
    cadastroFamiliaModal: {
      form: form,
      modalOpen: renderAfterRedirect,
      editionId,
    },
  },
})

export const mudancaResponsavelFamiliar = (
  familia: FamiliaFormModel,
  responsavel: CidadaoSelectFieldModel
): FamiliaFormModel => {
  const novoResponsavelPresenteFamilia = familia.cidadaos.some((cidadao) => cidadao.id === responsavel?.id)

  if (!novoResponsavelPresenteFamilia) {
    familia.cidadaos.push({
      _id: responsavel?.id,
      id: responsavel?.id,
      mudouSe: false,
      registroObito: undefined,
      isNovoCadastroNaFamilia: true,
    })
  }

  familia.responsavelId = responsavel?.id
  return familia
}

const pushIf = (campos: string[], cond: boolean, field: string) => {
  if (cond) campos.push(field)
}

const adicionarCamposNulosPorNacionalidadeBrasileira = (cidadao: CidadaoTerritorioModel, campos: string[]) => {
  if (cidadao?.nacionalidade?.nacionalidadeDbEnum === NacionalidadeEnum.BRASILEIRA) {
    pushIf(campos, isUndefinedOrNull(cidadao?.localidadeNascimento?.id), 'município de nascimento')
    pushIf(campos, isUndefinedOrNull(cidadao?.localidadeNascimento?.uf?.id), 'UF de nascimento')
    if (isUndefinedOrNull(cidadao?.cidadaoAldeado?.id)) {
      pushIf(campos, isUndefinedOrNull(cidadao?.telefoneCelular), 'telefone celular')
    }
  }
}

const adicionarCamposNulosPorNacionalidadeEstrangeira = (cidadao: CidadaoTerritorioModel, campos: string[]) => {
  if (cidadao?.nacionalidade?.nacionalidadeDbEnum === NacionalidadeEnum.ESTRANGEIRA) {
    pushIf(campos, isEmpty(cidadao?.paisNascimento), 'país de nascimento')
    pushIf(campos, isEmpty(cidadao?.dataEntradaBrasil), 'data de entrada no Brasil')
  }
}

const adicionarCamposNulosPorNacionalidadeNaturalizada = (cidadao: CidadaoTerritorioModel, campos: string[]) => {
  if (cidadao?.nacionalidade?.nacionalidadeDbEnum === NacionalidadeEnum.NATURALIZADA) {
    pushIf(campos, isEmpty(cidadao?.dataNaturalizacao), 'data de naturalização')
    pushIf(campos, isEmpty(cidadao?.portariaNaturalizacao), 'portaria de naturalização')
    if (isUndefinedOrNull(cidadao?.cidadaoAldeado?.id)) {
      pushIf(campos, isUndefinedOrNull(cidadao?.telefoneCelular), 'telefone celular')
    }
  }
}

export const getCamposNulosCidadaoTerritorioModel = (
  cidadao: CidadaoTerritorioModel,
  isResponsavel: boolean
): string[] => {
  const camposNulos: string[] = []

  if (isResponsavel && isEmpty(cidadao?.cpf) && isEmpty(cidadao?.cns)) {
    camposNulos.push('CPF ou CNS')
  }
  if (isEmpty(cidadao?.dataNascimento)) pushIf(camposNulos, true, 'data de nascimento')
  if (isEmpty(cidadao?.nome)) pushIf(camposNulos, true, 'nome')
  if (isEmpty(cidadao?.nomeMae) && !cidadao?.desconheceNomeMae) pushIf(camposNulos, true, 'nome da mãe')
  if (isEmpty(cidadao?.nomePai) && !cidadao?.desconheceNomePai) pushIf(camposNulos, true, 'nome do pai')
  if (cidadao?.racaCor?.racaCorDbEnum === RacaCorDbEnum.INDIGENA && isEmpty(cidadao?.etnia))
    pushIf(camposNulos, true, 'etnia')

  if (isEmpty(cidadao.cpf) && (!cidadao.justificativaNaoPossuiCpf || !cidadao.stNaoPossuiCpf)) {
    camposNulos.push('justificativa para não possuir CPF')
  }

  adicionarCamposNulosPorNacionalidadeBrasileira(cidadao, camposNulos)
  adicionarCamposNulosPorNacionalidadeEstrangeira(cidadao, camposNulos)
  adicionarCamposNulosPorNacionalidadeNaturalizada(cidadao, camposNulos)

  if (isEmpty(cidadao?.sexo)) pushIf(camposNulos, true, 'sexo')
  if (isEmpty(cidadao?.racaCor?.racaCorDbEnum)) pushIf(camposNulos, true, 'raça')
  if (isEmpty(cidadao?.nacionalidade)) pushIf(camposNulos, true, 'nacionalidade')
  return camposNulos.filterNotNull().sort()
}

export const verifyImoveisDuplicados = (
  previousEndereco: EnderecoFieldGroupModel,
  endereco: EnderecoFieldGroupModel
) => {
  const hasRequiredEndereco =
    endereco?.logradouro &&
    endereco?.bairro &&
    endereco?.cep &&
    endereco?.municipio &&
    endereco?.uf &&
    endereco?.tipoLogradouro &&
    (endereco?.numero || endereco?.semNumero)

  return (
    hasRequiredEndereco &&
    (!isEqual(endereco.logradouro, previousEndereco?.logradouro) ||
      !isEqual(endereco.bairro, previousEndereco?.bairro) ||
      endereco.cep !== previousEndereco?.cep ||
      !isEqual(endereco.tipoLogradouro, previousEndereco?.tipoLogradouro) ||
      endereco.numero !== previousEndereco?.numero ||
      endereco.semNumero !== previousEndereco?.semNumero ||
      !isEqual(endereco.municipio, previousEndereco?.municipio) ||
      !isEqual(endereco.uf, previousEndereco?.uf) ||
      endereco.complemento !== previousEndereco?.complemento)
  )
}

export const verifyImoveisIndigenasDuplicados = (
  previousEndereco: EnderecoIndigenaFieldGroupModel,
  endereco: EnderecoIndigenaFieldGroupModel
) => {
  const hasRequiredEnderecoIndigena =
    endereco?.dsei && endereco?.poloBase && endereco?.aldeia && endereco?.municipio && endereco?.uf && endereco?.numero

  return (
    hasRequiredEnderecoIndigena &&
    (!isEqual(endereco.dsei, previousEndereco?.dsei) ||
      !isEqual(endereco.poloBase, previousEndereco?.poloBase) ||
      !isEqual(endereco.aldeia, previousEndereco?.aldeia) ||
      !isEqual(endereco.municipio, previousEndereco?.municipio) ||
      !isEqual(endereco.uf, previousEndereco?.uf) ||
      endereco.numero !== previousEndereco?.numero ||
      endereco.numeroFamilia !== previousEndereco?.numeroFamilia)
  )
}
